/* eslint-disable no-shadow */
/* eslint-disable class-methods-use-this */
/* eslint-disable max-classes-per-file */

import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { PDFManagerFactory, WebViewerWrapper } from 'pdftron';
import {
  getManualSelectedAnnotationId,
  getManualSelectedZoneId,
  getNextMarqueeZoneId,
  inspection,
  app,
  getSelectedTool,
} from 'store';
import store from 'store/store';
import {
  DocumentTypes,
  MarqueeZoneCustomData,
  PDFTronTools,
  PDFTRON_DEFAULT_TOOL,
  AnnotationCustomData,
  InputAnnotation,
  AllDocumentTypes,
  ZoneCustomData,
} from 'types';
import AnnotationMixin from '../Annotation';
import ToolsMixin from '../Tools';
import { resetAllNonSelectableFlags, shakeDocumentsZones } from './AnnotationTools';
import drawMarkupLabel from './utils/drawMarkupLabel';
import GVAnnotationMixin from './utils/GVAnnotationMixin';
import { PREP_TOOL_LABEL, PREP_TOOL_ANNOTATION } from './utils/annotationConstants';
import { getPageRotation } from './utils/labelPositionUtils';
import quadsToDimensions from '../utils/quadsToDimensions';
import { annotationIsOutsideExistingCropZone, disableToolOutsideCropZones } from './utils/CropToolUtils';
import { useMarqueeZoneStateStore } from 'zstore/marqueeZoneStore';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { useZoomStateStore } from 'zstore/zoomStateStore';

class MarqueeZone {
  source: WebViewerWrapper | null = null;

  target: WebViewerWrapper | null = null;

  constructor(source: WebViewerWrapper | null, target: WebViewerWrapper | null) {
    this.source = source;
    this.target = target;
  }

  get toolsMixin() {
    return new ToolsMixin(this.source, this.target);
  }

  get annotationMixin() {
    return new AnnotationMixin(this.source, this.target);
  }

  async reloadMarqueeZones(sourceMarqueeAnnotations: InputAnnotation[], targetMarqueeAnnotations: InputAnnotation[]) {
    sourceMarqueeAnnotations.forEach(async (sourceMarquee, index) => {
      if (this.source && this.target) {
        await this.reDrawMarqueeZoneAnnotation(this.source, sourceMarquee.number || index + 1, sourceMarquee);
        // Get related annotation on target document
        const targetMarqueeZones = targetMarqueeAnnotations.filter((annot) => annot.number === sourceMarquee.number);

        await Promise.all(
          targetMarqueeZones.map(async (targetMarquee, index) => {
            if (this.target) {
              await this.reDrawMarqueeZoneAnnotation(this.target, targetMarquee.number || index + 1, targetMarquee);
            }
          }),
        );
      }
    });
    store.dispatch(
      inspection.actions.setMarqueeZoneId(
        sourceMarqueeAnnotations.length > 0
          ? Math.max(...sourceMarqueeAnnotations.map((marquee) => marquee.number || 0)) + 1
          : 1,
      ),
    );
  }

  async reDrawMarqueeZoneAnnotation(
    viewerWrapper: WebViewerWrapper,
    zoneNumber: number,
    marqueeAnnotation: InputAnnotation,
  ) {
    const MarqueeZoneAnnotation = this.createMarqueeZoneAnnotation(viewerWrapper.instance, viewerWrapper.documentType);
    const annotation = new MarqueeZoneAnnotation(zoneNumber);
    annotation.Id = marqueeAnnotation.annotationId;
    annotation.PageNumber = marqueeAnnotation.page;
    if (marqueeAnnotation.quads) {
      annotation.Quads = marqueeAnnotation.quads;
      const dimensionFromQuads = quadsToDimensions(annotation.Quads);
      annotation.setRect(
        new viewerWrapper.instance.Core.Math.Rect(
          dimensionFromQuads.X,
          dimensionFromQuads.Y,
          dimensionFromQuads.X + dimensionFromQuads.Width,
          dimensionFromQuads.Y + dimensionFromQuads.Height,
        ),
      );
    }
    annotation.ReadOnly = true;
    annotation.setCustomData(AnnotationCustomData.drawMarkups, 'true');
    viewerWrapper.annotationManager.addAnnotation(annotation);
    viewerWrapper.annotationManager.drawAnnotations({ pageNumber: annotation.PageNumber });
  }

  createMarqueeZoneAnnotation(instance: WebViewerInstance, documentType: AllDocumentTypes) {
    class MarqueeZoneAnnotation extends GVAnnotationMixin(instance.Core.Annotations.TextHighlightAnnotation) {
      constructor(zoneNumber: number | null = null) {
        super();
        const state = store.getState();
        const manualSelectedZoneId = getManualSelectedZoneId(state);
        const manualSelectedAnnotationId = getManualSelectedAnnotationId(state);
        this.setCustomData(MarqueeZoneCustomData.marqueeZoneAnotation, 'true');
        this.ToolName = PDFTronTools.MARQUEE_ZONE;
        this.IsHoverable = true;

        const addToZoneState = useMarqueeZoneStateStore.getState();
        const addToZone =
          documentType === DocumentTypes.source ? addToZoneState.sourceAddToZone : addToZoneState.targetAddToZone;

        if (addToZone) {
          this.Id = `${addToZone.id}_${Date.now()}`; // date is suffixed to make id unique
          this.setCustomData(MarqueeZoneCustomData.zoneId, `${addToZone.number}`);
        } else if (manualSelectedZoneId) {
          // ZONEID_ZONENUMBER ex. 2208636f-5f98-70be-eac7-6ed1c5f5f530_2
          this.Id = `${manualSelectedAnnotationId}_${manualSelectedZoneId}`;
          this.setCustomData(MarqueeZoneCustomData.zoneId, `${manualSelectedZoneId}`);
          this.ReadOnly = true;
        } else {
          this.setCustomData(MarqueeZoneCustomData.zoneId, `${zoneNumber || getNextMarqueeZoneId(state)}`);
        }
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        const realPageRotation = getPageRotation(instance, this.PageNumber);
        const selectedAnnotations = instance.Core.annotationManager.getSelectedAnnotations();
        const isSelected = selectedAnnotations.some((annot) => annot.Id === this.Id);

        const zoneSelected = selectedAnnotations.some(
          (annot) =>
            annot.getCustomData(MarqueeZoneCustomData.marqueeZoneAnotation) ||
            annot.getCustomData(ZoneCustomData.zoneAnnotation),
        );

        this.StrokeColor = new instance.Core.Annotations.Color(241, 160, 153, 0.5);
        // drawing box outline for marquee zones:
        // when no text/marquee zones are selected: all marquee zones will still have the outline
        // if a text/marquee zone is selected: marquee zones which are not selected will have their outline removed
        const { DEFAULT_FILL_STYLE, SELECTED_FILL_STYLE } = PREP_TOOL_ANNOTATION;

        const shouldDrawBox = !zoneSelected || isSelected;
        if (shouldDrawBox) {
          // Draw annotation rectangle with custom styling
          const rectangle = new Path2D();
          rectangle.rect(this.X, this.Y, this.Width, this.Height);
          ctx.strokeStyle = SELECTED_FILL_STYLE;
          ctx.stroke(rectangle);
        }

        super.draw(ctx, pageMatrix, pageRotation, zoomLevel);
        this.setStyles(ctx, pageMatrix);

        if (isSelected || (this as any)?.IsHovering) {
          const fillStyle = isSelected ? SELECTED_FILL_STYLE : DEFAULT_FILL_STYLE;

          const zoneNumber = parseInt(this.getCustomData(MarqueeZoneCustomData.zoneId), 10);
          const labelText = `Marquee Zone: ${zoneNumber}`;
          const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
          const pageWidth = instance.Core.documentViewer.getPageWidth(this.PageNumber);
          const pageHeight = instance.Core.documentViewer.getPageHeight(this.PageNumber);
          drawMarkupLabel(
            ctx,
            labelText,
            this.X,
            this.Y,
            HEIGHT,
            GAP,
            WIDTH_PADDING,
            fillStyle,
            zoomLevel,
            false,
            this.Width,
            this.Height,
            realPageRotation,
            pageWidth,
            pageHeight,
          );
        }
      }
    }
    return MarqueeZoneAnnotation;
  }

  createMarqueeZoneAnnotationTools() {
    if (this.source && this.target) {
      // @TODO instead of iterating here - we should probably call them twice, because even if we save some lines here, we are doing ifs inside to split the logic again.
      // We should modularize and call the procedure twice once per doctype.
      [this.source, this.target].forEach((wrapper: WebViewerWrapper) => {
        const { instance } = wrapper;
        const toolObject = new (class MarqueeZoneCreateTool extends instance.Core.Tools.TextHighlightCreateTool {
          constructor(marqueeZoneAnnotationTool: any) {
            super(instance.Core.documentViewer);
            instance.Core.Tools.TextAnnotationCreateTool.call(
              this,
              instance.Core.documentViewer,
              marqueeZoneAnnotationTool,
            );
          }

          // switchIn is called when this tool is selected
          switchIn(previousTool: any) {
            super.switchIn(previousTool);

            // changing cursor
            this.cursor = 'crosshair';
          }
        })(this.createMarqueeZoneAnnotation(instance, wrapper.documentType));

        disableToolOutsideCropZones(toolObject, wrapper);

        wrapper.registerTool({
          toolName: PDFTronTools.MARQUEE_ZONE,
          toolObject,
          buttonImage: '',
        });
      });
    }
  }

  getUnlinkMarqueePopup(instance: WebViewerInstance) {
    return {
      type: 'actionButton',
      img: '/icons/unmatchZonings.svg',
      title: 'Unlink',
      onClick: () => {
        if (!this.target || !this.source) return;

        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];

        const zoneNumber = selectedAnnotation.getCustomData(MarqueeZoneCustomData.zoneId);
        const targetAnnotsInZone = this.target?.getAnnotationList(
          (annotation) =>
            annotation.getCustomData(MarqueeZoneCustomData.marqueeZoneAnotation) === 'true' &&
            annotation.getCustomData(MarqueeZoneCustomData.zoneId) === zoneNumber,
        );

        const pendingZone = useMarqueeZoneStateStore.getState().pendingTargetMarqueeZoneNumber;
        if (targetAnnotsInZone && targetAnnotsInZone.length === 1) {
          if (pendingZone) {
            // if there are pending zones, we need to prompt the user to confirm the match
            const sourceSelected = this.source?.instance.Core.annotationManager.getSelectedAnnotations()[0];
            if (sourceSelected) {
              this.source?.annotationManager.deselectAnnotation(sourceSelected);
            }
            this.target?.annotationManager.deselectAnnotation(selectedAnnotation);
            // show popup One at a time ✋
            store.dispatch(
              inspection.actions.setChangeToolError(
                'Please confirm your match before creating or unmatching a new one.',
              ),
            );
            // get the pending source zone
            const pendingSourceZone = this.source
              ?.getAnnotationList()
              .filter(
                (annotation) =>
                  annotation.ToolName === PDFTronTools.MARQUEE_ZONE &&
                  parseInt(annotation.getCustomData(MarqueeZoneCustomData.zoneId), 10) === pendingZone,
              );
            if (pendingSourceZone && pendingSourceZone.length === 1) {
              this.source?.annotationManager.jumpToAnnotation(pendingSourceZone[0]);
              // the setTimeout is needed to ensure the annotation is properly selected
              setTimeout(() => {
                this.source?.annotationManager.selectAnnotation(pendingSourceZone[0]);
              }, 50);
            }
          } else {
            // set all other marquee zones as non-selectable
            const otherMarqueeZonesSource = this.source
              ?.getAnnotationList()
              .filter(
                (annotation) =>
                  (annotation.ToolName === PDFTronTools.MARQUEE_ZONE || annotation.ToolName === PDFTronTools.ZONE) &&
                  annotation.getCustomData(MarqueeZoneCustomData.zoneId) !== zoneNumber,
              );
            otherMarqueeZonesSource?.forEach((annotation) => {
              annotation.setCustomData(MarqueeZoneCustomData.nonSelectable, 'true');
            });
            const otherMarqueeZonesTarget = this.target
              ?.getAnnotationList()
              .filter(
                (annotation) =>
                  (annotation.ToolName === PDFTronTools.MARQUEE_ZONE || annotation.ToolName === PDFTronTools.ZONE) &&
                  annotation.getCustomData(MarqueeZoneCustomData.zoneId) !== zoneNumber,
              );
            otherMarqueeZonesTarget?.forEach((annotation) => {
              annotation.setCustomData(MarqueeZoneCustomData.nonSelectable, 'true');
            });
            instance.Core.annotationManager.deleteAnnotation(selectedAnnotation, { imported: false, force: true });
            store.dispatch(
              inspection.actions.deleteSelectedZone({
                documentType: DocumentTypes.target,
                annotationId: selectedAnnotation.Id,
              }),
            );
            this.promptMarqueeZoneCreation(DocumentTypes.target);
            // prompt the user to confirm the match
            store.dispatch(
              inspection.actions.activateManualSelection({
                annotationId: selectedAnnotation.Id,
                zoneId: parseInt(zoneNumber, 10),
              }),
            );
            // mark this zone as pending to enable CTA and navigation elsewhere
            useMarqueeZoneStateStore.getState().setPendingTargetMarqueeZoneNumber(parseInt(zoneNumber, 10));
            // expire any existing toasts before showing a new unlink warning
            const { expireAllToast } = useDocumentToastStateStore.getState();
            expireAllToast();
            const { setDocumentToast } = useDocumentToastStateStore.getState();
            setDocumentToast(DocumentTypes.source, {
              message: `Unlinked Marquee Zone #${zoneNumber}. Resolve this zone by either deleting it on the Source document, or creating a matching zone in the Sample document.`,
              type: 'warning',
              expired: false,
            });
          }
        } else {
          instance.Core.annotationManager.deleteAnnotation(selectedAnnotation, { imported: false, force: true });
          store.dispatch(
            inspection.actions.deleteSelectedZone({
              documentType: DocumentTypes.target,
              annotationId: selectedAnnotation.Id,
            }),
          );
          //update inspection input
          this.annotationMixin.updateInputAnnotations();
        }
      },
    };
  }

  getAddToMarqueeZonePopup(documentType: DocumentTypes, instance: WebViewerInstance) {
    return {
      type: 'actionButton',
      img: '/icons/addToZone.svg',
      title: 'Add To Zone',
      onClick: () => {
        const annotations = instance.Core.annotationManager.getSelectedAnnotations();
        const selectedAnnotation = annotations[0];
        const zoneNumber = parseInt(selectedAnnotation.getCustomData(MarqueeZoneCustomData.zoneId), 10);

        if (documentType === DocumentTypes.source) {
          useMarqueeZoneStateStore.getState().setSourceAddToZone({ number: zoneNumber, id: selectedAnnotation.Id });
          useMarqueeZoneStateStore.getState().setTargetAddToZone(null);
          this.promptMarqueeZoneCreation(DocumentTypes.source);
        } else {
          useMarqueeZoneStateStore.getState().setTargetAddToZone({ number: zoneNumber, id: selectedAnnotation.Id });
          useMarqueeZoneStateStore.getState().setSourceAddToZone(null);
          this.promptMarqueeZoneCreation(DocumentTypes.target);
        }
        this.source?.annotationManager.deselectAllAnnotations();
        this.target?.annotationManager.deselectAllAnnotations();
      },
    };
  }

  getDeleteMarqueePopup(instance: WebViewerInstance) {
    return {
      type: 'actionButton',
      img: '/icons/zoneDelete.svg',
      title: 'Delete',
      onClick: () => {
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
        this.deleteMarqueeZoneByAnnotation(selectedAnnotation);
      },
    };
  }

  // Public helper to delete a marquee zone using the same flow as the popup,
  // but operating on an explicit annotation (no reliance on current selection)
  public deleteMarqueeZoneByAnnotation(
    annotationToDelete: Core.Annotations.Annotation | undefined,
    deleteAllSource: boolean = false,
  ): void {
    if (!annotationToDelete) return;
    const instance = this.source?.instance;
    if (!instance || !this.target) return;

    const zoneNumber = annotationToDelete.getCustomData(MarqueeZoneCustomData.zoneId);

    // delete selected source annotation
    instance.Core.annotationManager.deleteAnnotation(annotationToDelete, { imported: false, force: true });

    // Gather remaining source annots for this zoneId once
    let remainingSourceAnnots = this.source?.getAnnotationList(
      (annotation) =>
        annotation.ToolName === PDFTronTools.MARQUEE_ZONE &&
        annotation.getCustomData(MarqueeZoneCustomData.zoneId) === zoneNumber,
    );

    if (deleteAllSource && remainingSourceAnnots?.length) {
      this.source?.annotationManager.deleteAnnotations(remainingSourceAnnots, { imported: false, force: true });
      store.dispatch(
        inspection.actions.deleteSelectedZones({
          documentType: DocumentTypes.source,
          annotationIds: remainingSourceAnnots.map((a) => a.Id),
        }),
      );
      remainingSourceAnnots = [];
    }

    const noRemainingSourceAnnotsInZone = (remainingSourceAnnots?.length || 0) === 0;
    if (noRemainingSourceAnnotsInZone) {
      // fecth all target annotations in zone
      const targetAnnotations = this.target?.getAnnotationList(
        (annotation) =>
          annotation.getCustomData(MarqueeZoneCustomData.marqueeZoneAnotation) === 'true' &&
          annotation.getCustomData(MarqueeZoneCustomData.zoneId) === zoneNumber,
      );
      if (targetAnnotations) {
        // delete all target annotations in zone
        this.target?.annotationManager.deleteAnnotations(targetAnnotations, { imported: false, force: true });
      }
      store.dispatch(
        inspection.actions.deleteSelectedZones({
          documentType: DocumentTypes.target,
          annotationIds: targetAnnotations?.length ? targetAnnotations.map((a) => a.Id) : [],
        }),
      );

      // if user deleted the pending zone, we prompt the user to create a new one
      // only when marquee tool is currently selected
      const selectedTool = getSelectedTool(store.getState());
      if (selectedTool === PDFTronTools.MARQUEE_ZONE) {
        this.promptMarqueeZoneCreation(DocumentTypes.source);
      }
      store.dispatch(inspection.actions.deactivateManualSelection());
      useMarqueeZoneStateStore.getState().setPendingTargetMarqueeZoneNumber(0);
      // expire any outstanding document toasts related to unlink flow
      const { expireAllToast } = useDocumentToastStateStore.getState();
      expireAllToast();
      store.dispatch(
        inspection.actions.deleteSelectedZone({
          documentType: DocumentTypes.source,
          annotationId: annotationToDelete.Id,
        }),
      );

      // reset nonSelectable on remaining zones (text and marquee)
      this.annotationMixin.updateInputAnnotations(false);

      resetAllNonSelectableFlags();
      shakeDocumentsZones(annotationToDelete);
    }

    // Updates annotations in redux (later get saved in the db)
    this.annotationMixin.updateInputAnnotations();
  }

  marqueeAnnotationSelected(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) {
    // Prevent selecting annotations marked as non-selectable during unlink/manual flow
    if (annotation.getCustomData(MarqueeZoneCustomData.nonSelectable) === 'true') {
      if (documentType === DocumentTypes.source) {
        this.source?.instance.UI.annotationPopup.update([]);
        this.source?.annotationManager.deselectAnnotation(annotation);
      } else {
        this.target?.instance.UI.annotationPopup.update([]);
        this.target?.annotationManager.deselectAnnotation(annotation);
      }

      // Identify any active unconfirmed zone (Text or Marquee)
      const pendingMarquee = useMarqueeZoneStateStore.getState().pendingTargetMarqueeZoneNumber;
      const sourceList = this.source?.instance.Core.annotationManager.getAnnotationsList() || [];
      const unconfirmedText = sourceList.find(
        (annot) => annot.ToolName === PDFTronTools.ZONE && annot.getCustomData(ZoneCustomData.confirmed) === 'false',
      );
      const unconfirmedMarquee = pendingMarquee
        ? sourceList.find(
            (annot) =>
              annot.ToolName === PDFTronTools.MARQUEE_ZONE &&
              parseInt(annot.getCustomData(MarqueeZoneCustomData.zoneId), 10) === pendingMarquee &&
              annot.Id !== annotation.Id, // it's not the one I'm selecting
          )
        : undefined;

      const focusAnnot = unconfirmedText || unconfirmedMarquee;
      if (focusAnnot) {
        this.source?.annotationManager.jumpToAnnotation(focusAnnot);
        setTimeout(() => this.source?.annotationManager.selectAnnotation(focusAnnot), 50);
        if (focusAnnot.ToolName === PDFTronTools.MARQUEE_ZONE) {
          this.promptMarqueeZoneCreation(DocumentTypes.target);
        } else {
          // Switch to text manual flow
          const pdfDocManager: any = PDFManagerFactory.getPDFDocManager?.();
          pdfDocManager.promptTextZoneCreation(DocumentTypes.target);
          store.dispatch(
            inspection.actions.activateManualSelection({
              annotationId: focusAnnot.Id,
              zoneId: parseInt(focusAnnot.getCustomData(ZoneCustomData.zoneId), 10),
            }),
          );
        }
      }

      return;
    }
    // select source annotations
    const zoneNumber = annotation.getCustomData(MarqueeZoneCustomData.zoneId);
    const sourceAnnotations = this.source?.getAnnotationList(
      (annot) =>
        annot.getCustomData(MarqueeZoneCustomData.marqueeZoneAnotation) === 'true' &&
        annot.getCustomData(MarqueeZoneCustomData.zoneId) === zoneNumber &&
        annot.getCustomData(MarqueeZoneCustomData.nonSelectable) !== 'true',
    );
    // Zoom and center on the selected zone (both documents when available)
    const { zoomLockLevel, setZoomChanging } = useZoomStateStore.getState();
    if (sourceAnnotations?.length) {
      this.source?.setDifferenceZoomLevel(sourceAnnotations[0], !!zoomLockLevel[DocumentTypes.source]);
      this.source?.jumpToAnnotation(sourceAnnotations[0].Id);
      setZoomChanging();

      if (sourceAnnotations.some((sourceAnnot) => !this.source?.annotationManager.isAnnotationSelected(sourceAnnot))) {
        this.source?.annotationManager.selectAnnotations(sourceAnnotations);
      }
    }

    // select target annotations
    const targetAnnotations = this.target?.getAnnotationList(
      (annot) =>
        annot.getCustomData(MarqueeZoneCustomData.marqueeZoneAnotation) === 'true' &&
        annot.getCustomData(MarqueeZoneCustomData.zoneId) === zoneNumber &&
        annot.getCustomData(MarqueeZoneCustomData.nonSelectable) !== 'true',
    );
    if (targetAnnotations?.length) {
      this.target?.setDifferenceZoomLevel(targetAnnotations[0], !!zoomLockLevel[DocumentTypes.target]);
      this.target?.jumpToAnnotation(targetAnnotations[0].Id);
      setZoomChanging();

      if (targetAnnotations.some((targetAnnot) => !this.target?.annotationManager.isAnnotationSelected(targetAnnot))) {
        this.target?.annotationManager.selectAnnotations(targetAnnotations);
      }
    }

    if (documentType === DocumentTypes.source) {
      // show delete button
      if (this.source) {
        this.source.instance.UI.annotationPopup.update([
          this.getAddToMarqueeZonePopup(DocumentTypes.source, this.source.instance),
          this.getDeleteMarqueePopup(this.source.instance),
        ]);
      }
    } else {
      // show confirm / unlink buttons
      const instance = this.target?.instance;
      if (instance) {
        instance.UI.annotationPopup.update([
          this.getAddToMarqueeZonePopup(DocumentTypes.target, instance),
          this.getUnlinkMarqueePopup(instance),
        ]);
      }
    }
  }

  // called from saga @todo add not confirmed zone handling
  marqueeAnnotationChanged(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) {
    if (annotation.getCustomData(AnnotationCustomData.drawMarkups) === 'true') return;
    switch (action) {
      case 'add': {
        const addToZoneState = useMarqueeZoneStateStore.getState();
        const addToZone =
          documentType === DocumentTypes.source ? addToZoneState.sourceAddToZone : addToZoneState.targetAddToZone;

        // forbidden path: creating source marquee zone while waiting for target marquee zone
        if (
          documentType === DocumentTypes.source &&
          useMarqueeZoneStateStore.getState().pendingTargetMarqueeZoneNumber &&
          !addToZone
        ) {
          // delete any new annotation and let the user
          this.source?.annotationManager.deleteAnnotation(annotation, {
            imported: false,
            force: true,
            isUndoRedo: false,
          });
          store.dispatch(inspection.actions.setUnconfirmedMarqueeZoneError(true));
          return;
        }

        if (addToZone) {
          // handle add to existing zone
          useMarqueeZoneStateStore.getState().setSourceAddToZone(null);
          useMarqueeZoneStateStore.getState().setTargetAddToZone(null);
          this.promptMarqueeZoneCreation(
            useMarqueeZoneStateStore.getState().pendingTargetMarqueeZoneNumber
              ? DocumentTypes.target
              : DocumentTypes.source,
          );
        } else {
          if (documentType === DocumentTypes.source) {
            // handle new zone creation
            const zoneId = annotation.getCustomData(MarqueeZoneCustomData.zoneId);
            store.dispatch(
              inspection.actions.activateManualSelection({
                annotationId: annotation.Id,
                zoneId: parseInt(zoneId, 10),
              }),
            );
            useMarqueeZoneStateStore.getState().setPendingTargetMarqueeZoneNumber(parseInt(zoneId, 10));
          } else {
            store.dispatch(inspection.actions.deactivateManualSelection());
            store.dispatch(inspection.actions.increaseMarqueeZoneId());
            useMarqueeZoneStateStore.getState().setPendingTargetMarqueeZoneNumber(0);
            resetAllNonSelectableFlags();
          }

          this.promptMarqueeZoneCreation(
            documentType === DocumentTypes.source ? DocumentTypes.target : DocumentTypes.source,
          );
        }

        // we need to select the annotation after we create it
        if (documentType === DocumentTypes.source) {
          this.source?.annotationManager.selectAnnotation(annotation);
        } else {
          this.target?.annotationManager.selectAnnotation(annotation);
        }
        // confirming target resolves unlink warning: expire document toasts
        if (documentType === DocumentTypes.target) {
          const { expireAllToast } = useDocumentToastStateStore.getState();
          expireAllToast();
        }
        // Updates annotations in redux (later get saved in the db)
        this.annotationMixin.updateInputAnnotations();
        break;
      }
      default:
    }
  }

  private promptMarqueeZoneCreation(documentType: DocumentTypes) {
    const activeDocument = PDFManagerFactory.getViewer(documentType);
    const inactiveDocument = PDFManagerFactory.getViewer(
      documentType === DocumentTypes.source ? DocumentTypes.target : DocumentTypes.source,
    );
    if (!activeDocument || !inactiveDocument) {
      return;
    }

    activeDocument.addDocumentHighlight();
    activeDocument.setTool(PDFTronTools.MARQUEE_ZONE);
    inactiveDocument.removeAnnotationDocumentHighlight();
    inactiveDocument.setTool(PDFTRON_DEFAULT_TOOL);

    // Update Redux store to reflect the tool selection in the UI
    store.dispatch(inspection.actions.setSelectedTool({ tool: PDFTronTools.MARQUEE_ZONE }));
  }
}

export default MarqueeZone;
